import React, { useState, useEffect } from 'react';
import { getStatusDisplayName, getStatusColor, getStatusIcon } from '../constants/statusTypes';
import { useMetrics } from '../contexts/MetricsContext';
import './LogMetricModal.css';

const LogMetricModal = ({ isOpen, onClose, onSave, metric }) => {
  const [date, setDate] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [numericValue, setNumericValue] = useState('');
  const [notes, setNotes] = useState('');
  const [calculatedStatus, setCalculatedStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { checkDailyEntryExists } = useMetrics();

  // Set today's date when modal opens
  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      setDate(`${year}-${month}-${day}`);
      setSelectedOption('');
      setNumericValue('');
      setNotes('');
      setCalculatedStatus(null);
      setError('');
    }
  }, [isOpen]);

  // Calculate status based on metric type and input
  useEffect(() => {
    if (!metric) return;

    if (metric.type === 'list' && selectedOption) {
      const option = metric.statusConfig.options.find(opt => opt.name === selectedOption);
      if (option) {
        setCalculatedStatus(option.status);
      }
    } else if (metric.type === 'numeric' && numericValue !== '') {
      const value = parseFloat(numericValue);
      if (!isNaN(value)) {
        const { statusConfig } = metric;
        
        // Check operational condition
        if (statusConfig.operational.operator === 'range') {
          if (value >= statusConfig.operational.min && value <= statusConfig.operational.max) {
            setCalculatedStatus('operational');
            return;
          }
        } else if (statusConfig.operational.operator === '>=' && value >= statusConfig.operational.value) {
          setCalculatedStatus('operational');
          return;
        } else if (statusConfig.operational.operator === '>' && value > statusConfig.operational.value) {
          setCalculatedStatus('operational');
          return;
        } else if (statusConfig.operational.operator === '=' && value === statusConfig.operational.value) {
          setCalculatedStatus('operational');
          return;
        } else if (statusConfig.operational.operator === '<=' && value <= statusConfig.operational.value) {
          setCalculatedStatus('operational');
          return;
        } else if (statusConfig.operational.operator === '<' && value < statusConfig.operational.value) {
          setCalculatedStatus('operational');
          return;
        }
        
        // Check degraded condition
        if (statusConfig.degraded.operator === 'range') {
          if (value >= statusConfig.degraded.min && value <= statusConfig.degraded.max) {
            setCalculatedStatus('degraded');
            return;
          }
        } else if (statusConfig.degraded.operator === '>=' && value >= statusConfig.degraded.value) {
          setCalculatedStatus('degraded');
          return;
        } else if (statusConfig.degraded.operator === '>' && value > statusConfig.degraded.value) {
          setCalculatedStatus('degraded');
          return;
        } else if (statusConfig.degraded.operator === '=' && value === statusConfig.degraded.value) {
          setCalculatedStatus('degraded');
          return;
        } else if (statusConfig.degraded.operator === '<=' && value <= statusConfig.degraded.value) {
          setCalculatedStatus('degraded');
          return;
        } else if (statusConfig.degraded.operator === '<' && value < statusConfig.degraded.value) {
          setCalculatedStatus('degraded');
          return;
        }
        
        // Default to outage
        setCalculatedStatus('outage');
      }
    } else {
      setCalculatedStatus(null);
    }
  }, [metric, selectedOption, numericValue]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !calculatedStatus) {
      setError('Please fill in all required fields');
      return;
    }

    // Check if an entry already exists for this date
    if (checkDailyEntryExists(metric.id, date)) {
      setError('An entry already exists for this date. Please choose a different date or edit the existing entry.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const logEntry = {
        metricId: metric.id,
        date: date,
        status: calculatedStatus,
        value: metric.type === 'numeric' ? parseFloat(numericValue) : selectedOption,
        notes: notes.trim(),
        timestamp: new Date().toISOString()
      };

      await onSave(logEntry);
      onClose();
    } catch (error) {
      setError(error.message || 'Failed to save log entry');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Log Entry - {metric?.name}</h3>
          <button 
            className="close-btn"
            onClick={handleClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="log-form">
          <div className="form-group">
            <label htmlFor="date">Date</label>
            <input
              type="date"
              id="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          {metric?.type === 'list' && (
            <div className="form-group">
              <label htmlFor="option">Option</label>
              <select
                id="option"
                value={selectedOption}
                onChange={(e) => setSelectedOption(e.target.value)}
                required
                disabled={loading}
              >
                <option value="">Select an option</option>
                {metric.statusConfig.options.map((option, index) => (
                  <option key={index} value={option.name}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {metric?.type === 'numeric' && (
            <div className="form-group">
              <label htmlFor="value">Value ({metric.unit})</label>
              <input
                type="number"
                id="value"
                value={numericValue}
                onChange={(e) => setNumericValue(e.target.value)}
                step="0.1"
                required
                disabled={loading}
                placeholder={`Enter ${metric.name.toLowerCase()} in ${metric.unit}`}
              />
            </div>
          )}

          <div className="status-display">
            <label>Calculated Status:</label>
            <div 
              className="status-indicator"
              style={{ color: calculatedStatus ? getStatusColor(calculatedStatus) : '#999' }}
            >
              {calculatedStatus ? (
                <>
                  {getStatusIcon(calculatedStatus)} {getStatusDisplayName(calculatedStatus)}
                </>
              ) : (
                <span className="status-placeholder">
                  {metric?.type === 'list' 
                    ? 'Select an option above to see status' 
                    : 'Enter a value above to see status'
                  }
                </span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes (optional)</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              placeholder="Add any additional notes about this entry..."
              rows={3}
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-btn"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="save-btn"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogMetricModal;
